import React, { useCallback, useEffect } from 'react';
import { RequiredIndication, SelectBox, TextInput } from '../../components/design-system/forms';
import styles from './user-profile.module.scss';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useProfileOptions, useProfileRequirementSets } from 'shared/hooks/profile.hooks';
import { DateTime } from 'luxon';
import { Col, notification, Row } from 'antd';
import ReactMarkdown from 'react-markdown';
import Checkbox from 'antd/es/checkbox/Checkbox';
import { LabelsSelector } from '../labels/labels-selector';
import { ProfileLayoutComponent } from '../profiles/profile-layout.component';
import { UserAvatar } from '../avatar';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Button, Divider, Notification, ScreenTitle } from '../design-system';
import { Loader } from '../core';
import { isAccountAdmin } from 'shared/store/selectors/auth.selector';
import { ProfessionalProfileManager } from '../profiles/professional-profile-manager';

const PROFILE_TYPES = {
  USER: 'USER',
  CONTACT: 'CONTACT',
  SELF: 'SELF',
};

// Stable default so the `schema` useMemo below doesn't recompute every render
// when no schema prop is passed (a fresh `{}` default would be a new reference
// each render).
const EMPTY_SCHEMA = Yup.object().shape({});

const UserProfileComponent = ({
  user,
  selectedLang,
  isPopup = false,
  onClose = () => {},
  showTermsAndConditions = false,
  type = PROFILE_TYPES.SELF,
  schema: initialSchema = EMPTY_SCHEMA,
  onUpdate = () => {},
  analyticsTriggers = {},
}) => {
  const [loading, setLoading] = React.useState(false);
  const [api, contextHolder] = notification.useNotification();
  const isAdmin = useSelector(isAccountAdmin);
  const canEdit = type === PROFILE_TYPES.SELF || (type !== PROFILE_TYPES.CONTACT && isAdmin);
  const { professional = {}, profession, ...userData } = user;
  const enforceTNC = isPopup && showTermsAndConditions;
  const navigate = useNavigate();
  const lang = useSelector(getLang('USER_PROFILE'));

  const { professionsWithRoles, rolesWithLevels, configuredFields } = useProfileRequirementSets();

  const schema = React.useMemo(() => {
    const base = typeof initialSchema?.concat === 'function' ? initialSchema : EMPTY_SCHEMA;

    // A dependent field is required only when the selected parent actually has
    // child options (per-org, from useProfileRequirementSets).
    let next = base.concat(
      Yup.object().shape({
        role: Yup.string().when('profession', ([profession], roleSchema) =>
          professionsWithRoles.has(profession)
            ? roleSchema.required('Required!')
            : roleSchema.nullable(),
        ),
        levelOfTraining: Yup.string().when('role', ([role], levelSchema) =>
          rolesWithLevels.has(role) ? levelSchema.required('Required!') : levelSchema.nullable(),
        ),
      }),
    );

    // profession / department are required in the static schema, but a per-org
    // field the org hasn't configured is hidden and can never be filled — so
    // relax it to optional. Without this an org with no profile fields (user
    // only accepts the T&C) leaves the form invalid and Save permanently
    // disabled.
    const relaxedRequired = {};
    if (!configuredFields.has('profession')) relaxedRequired.profession = Yup.string().nullable();
    if (!configuredFields.has('department')) relaxedRequired.department = Yup.string().nullable();
    if (Object.keys(relaxedRequired).length > 0) {
      next = next.concat(Yup.object().shape(relaxedRequired));
    }

    if (enforceTNC) {
      next = next.concat(
        Yup.object().shape({
          approveTermsAndConditions: Yup.boolean().oneOf([true]).required('Required!'),
        }),
      );
    }

    return next;
  }, [enforceTNC, initialSchema, professionsWithRoles, rolesWithLevels, configuredFields]);

  useEffect(() => {
    analyticsTriggers?.openProfile?.({
      id: user.id.toString(),
      email: user.email,
    });
  }, [user, analyticsTriggers]);

  const openNotification = useCallback(() => {
    api.success({
      title: lang.NOTIFICATION_SUCCESS,
      placement: 'bottomRight',
    });
  }, [api, lang]);

  const onFinish = useCallback(() => {
    if (isPopup || type === PROFILE_TYPES.SELF) {
      onClose();
      return;
    }
    navigate(-1);
  }, [isPopup, onClose, navigate, type]);

  const updateProfile = useCallback(
    async data => {
      setLoading(true);
      const res = await onUpdate({ id: user.id, data });

      if (!res.error && !res.errors) {
        const payload = {
          id: user.id.toString(),
          email: data.email,
          profession: data?.profession,
          role: data?.role,
          levelOfTraining: data?.levelOfTraining,
          department: data?.department,
          division: data?.division,
          unit: data?.unit,
          location: data?.location,
          title: data?.title,
          specialty: data?.specialty,
          rotation: data?.rotation,
          language: data?.language,
          timezone: data?.timezone,
        };

        if (!user.profileCompletedAt) {
          analyticsTriggers?.completeProfile?.(payload);
        } else {
          analyticsTriggers?.updateProfile?.(payload);
        }

        openNotification();
        onFinish();
        setLoading(false);
        return;
      }

      api.error({
        title: lang.NOTIFICATION_ERROR,
        placement: 'bottomRight',
      });

      setLoading(false);
    },
    [onFinish, openNotification, onUpdate, user, analyticsTriggers, lang, api],
  );

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm,
    values,
    errors,
    touched,
    dirty,
    isValid,
    validateForm,
  } = useFormik({
    validationSchema: schema,
    initialValues: {
      ...userData,
      ...professional,
      profession,
      timezone: user?.timezone ?? DateTime.local().zoneName,
      language: user?.language ?? selectedLang,
    },
    onSubmit: updateProfile,
    enableReinitialize: true,
  });

  // Re-validate on mount and whenever the form reinitializes (`enableReinitialize`
  // picks up the post-mount `/me` merge into `user`) or the per-org validation
  // `schema` finishes loading. Formik's reinitialize resets errors *without*
  // re-validating, so `isValid` would otherwise flip stale-`true` after the merge
  // — leaving an incomplete popup's Save button wrongly enabled.
  useEffect(() => {
    validateForm();
  }, [validateForm, user, schema]);

  const handleDependentFieldChange = useCallback(
    (field, dependentField, value) => {
      if (!value || value !== values[field]) {
        setFieldValue(dependentField, '');
      }
      setFieldValue(field, value);
    },
    [setFieldValue, values],
  );

  const onChange = useCallback(
    (field, value) => {
      if (field === 'profession') {
        handleDependentFieldChange('profession', 'role', value);
        handleDependentFieldChange('role', 'levelOfTraining');
      }

      if (field === 'role') {
        handleDependentFieldChange('role', 'levelOfTraining', value);
      }

      setFieldValue(field, value);
    },
    [handleDependentFieldChange, setFieldValue],
  );

  const changeTNCApproval = useCallback(() => {
    onChange('approveTermsAndConditions', !values.approveTermsAndConditions);
  }, [onChange, values.approveTermsAndConditions]);

  const {
    departmentOptions,
    divisionOptions,
    specialtyOptions,
    rotationOptions,
    professionOptions,
    roleOptions,
    levelOftrainingOptions,
    unitOptions,
    languageOptions,
    fieldLabels,
    fieldHasOptions,
  } = useProfileOptions({
    profession: values.profession,
    role: values.role,
    department: values.department,
  });

  const fieldLabel = (key, fallback) => fieldLabels?.[key] || fallback;

  const isRoleRequired = professionsWithRoles.has(values.profession);
  const isLevelOfTrainingRequired = rolesWithLevels.has(values.role);
  const isUser = type === PROFILE_TYPES.USER || type === PROFILE_TYPES.SELF;

  const formComponents = [
    {
      Component: TextInput,
      props: {
        label:
          type === PROFILE_TYPES.SELF || type === PROFILE_TYPES.CONTACT ? (
            <RequiredIndication label={lang.FIRST_NAME} />
          ) : (
            lang.FIRST_NAME
          ),
        placeholder: lang.FIRST_NAME,
        name: 'firstName',
        value: values.firstName,
        error: errors.firstName,
        touched: touched.firstName,
        onChange: handleChange,
        onBlur: handleBlur,
        required: type === PROFILE_TYPES.SELF || type === PROFILE_TYPES.CONTACT,
      },
    },
    {
      Component: TextInput,
      props: {
        label:
          type === PROFILE_TYPES.SELF ? (
            <RequiredIndication label={lang.LAST_NAME} />
          ) : (
            lang.LAST_NAME
          ),
        placeholder: lang.LAST_NAME,
        name: 'lastName',
        value: values.lastName,
        error: errors.lastName,
        touched: touched.lastName,
        onChange: handleChange,
        onBlur: handleBlur,
        required: type === PROFILE_TYPES.SELF,
      },
    },
    {
      Component: TextInput,
      props: {
        allowClear: true,
        label: lang.EMAIL,
        placeholder: lang.EMAIL,
        name: 'email',
        value: values.email,
        error: errors.email,
        touched: touched.email,
        onChange: handleChange,
        onBlur: handleBlur,
        disabled: isUser,
      },
    },
    {
      Component: TextInput,
      props: {
        allowClear: true,
        label: lang.PHONE,
        placeholder: lang.PHONE,
        name: 'phone',
        value: values.phone,
        error: errors.phone,
        touched: touched.phone,
        onChange: handleChange,
        onBlur: handleBlur,
      },
    },
    {
      Component: TextInput,
      props: {
        allowClear: true,
        label: lang.PAGER,
        placeholder: lang.PAGER,
        name: 'pager',
        value: values.pager,
        error: errors.pager,
        touched: touched.pager,
        onChange: handleChange,
        onBlur: handleBlur,
      },
    },
    {
      Component: TextInput,
      props: {
        allowClear: true,
        label: lang.CISCO,
        placeholder: lang.CISCO,
        name: 'cisco',
        value: values.cisco,
        error: errors.cisco,
        touched: touched.cisco,
        onChange: handleChange,
        onBlur: handleBlur,
      },
    },
    {
      Component: SelectBox,
      props: {
        label:
          type === PROFILE_TYPES.SELF ? (
            <RequiredIndication label={fieldLabel('department', lang.DEPARTMENT)} />
          ) : (
            fieldLabel('department', lang.DEPARTMENT)
          ),
        placeholder: fieldLabel('department', lang.DEPARTMENT),
        name: 'department',
        value: values.department,
        error: errors.department,
        onChange: onChange,
        options: departmentOptions,
        required: type === PROFILE_TYPES.SELF,
      },
      hide: !fieldHasOptions.department,
    },
    {
      Component: SelectBox,
      props: {
        label:
          type === PROFILE_TYPES.SELF ? (
            <RequiredIndication label={fieldLabel('profession', lang.PROFESSION)} />
          ) : (
            fieldLabel('profession', lang.PROFESSION)
          ),
        placeholder: fieldLabel('profession', lang.PROFESSION),
        name: 'profession',
        value: values.profession,
        error: errors.profession,
        onChange: onChange,
        options: professionOptions,
        required: type === PROFILE_TYPES.SELF,
      },
      hide: !isUser || !fieldHasOptions.profession,
    },
    {
      Component: SelectBox,
      props: {
        label: isRoleRequired ? (
          <RequiredIndication label={fieldLabel('role', lang.ROLE)} />
        ) : (
          fieldLabel('role', lang.ROLE)
        ),
        placeholder: fieldLabel('role', lang.ROLE),
        name: 'role',
        value: values.role,
        error: errors.role,
        onChange: onChange,
        options: roleOptions ?? [],
        disabled: !roleOptions?.length,
        required: isRoleRequired,
      },
      hide: !isUser || !fieldHasOptions.role,
    },
    {
      Component: SelectBox,
      props: {
        label: isLevelOfTrainingRequired ? (
          <RequiredIndication label={fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING)} />
        ) : (
          fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING)
        ),
        placeholder: fieldLabel('levelOfTraining', lang.LEVEL_OF_TRAINING),
        name: 'levelOfTraining',
        value: values.levelOfTraining,
        error: errors.levelOfTraining,
        onChange: onChange,
        options: levelOftrainingOptions ?? [],
        disabled: !levelOftrainingOptions?.length,
        required: isLevelOfTrainingRequired,
      },
      hide: !isUser || !fieldHasOptions.levelOfTraining,
    },
    {
      Component: SelectBox,
      props: {
        allowClear: true,
        label: lang.ROTATION,
        placeholder: lang.ROTATION,
        name: 'rotation',
        value: values.rotation,
        error: errors.rotation,
        onChange: onChange,
        options: rotationOptions,
      },
      hide: !isUser,
    },
    {
      Component: SelectBox,
      props: {
        allowClear: true,
        mode: 'multiple',
        label: fieldLabel('division', lang.DIVISION),
        placeholder: fieldLabel('division', lang.DIVISION),
        name: 'division',
        value: values.division,
        error: errors.division,
        onChange: onChange,
        options: divisionOptions,
      },
      hide: !isUser || !fieldHasOptions.division,
    },
    {
      Component: LabelsSelector,
      props: {
        allowClear: true,
        label: lang.LOCATION,
        value: values.location,
        error: errors.location,
        placeholder: lang.LOCATION,
        name: 'location',
        onChange: onChange,
        wrapperClass: styles.location,
      },
    },
    {
      Component: SelectBox,
      props: {
        allowClear: true,
        mode: 'multiple',
        label: fieldLabel('specialty', lang.SPECIALTY),
        placeholder: fieldLabel('specialty', lang.SPECIALTY),
        name: 'specialty',
        onChange: onChange,
        options: specialtyOptions,
        value: values.specialty,
        error: errors.specialty,
      },
      hide: !isUser || !fieldHasOptions.specialty,
    },
    {
      Component: SelectBox,
      props: {
        allowClear: true,
        mode: 'multiple',
        label: fieldLabel('unit', lang.UNIT),
        placeholder: fieldLabel('unit', lang.UNIT),
        name: 'unit',
        value: values.unit,
        error: errors.unit,
        onChange: onChange,
        options: unitOptions,
      },
      hide: !isUser || !fieldHasOptions.unit,
    },
    {
      Component: TextInput,
      props: {
        allowClear: true,
        label: lang.TITLE,
        placeholder: lang.TITLE,
        name: 'title',
        value: values.title,
        error: errors.title,
        touched: touched.title,
        onChange: handleChange,
        onBlur: handleBlur,
      },
      hide: !isUser,
    },
    {
      Component: SelectBox,
      props: {
        label: lang.LANGUAGE,
        placeholder: lang.LANGUAGE,
        name: 'language',
        value: values.language,
        error: errors.language,
        onChange: onChange,
        options: languageOptions,
      },
      hide: type !== PROFILE_TYPES.SELF,
    },
  ];

  const topSectionFieldsOrder = ['firstName', 'lastName'];
  const userFieldsOrder = [
    'email',
    'title',
    'department',
    'phone',
    'profession',
    'division',
    'cisco',
    'role',
    'location',
    'pager',
    'levelOfTraining',
    'rotation',
    'language',
    'specialty',
    'unit',
  ];

  const renderNotificationButtons = () => {
    const disableSave = !isPopup && !dirty;

    return (
      <div className={styles.notificationButtons}>
        <Button onClick={resetForm}>{lang.DISCARD}</Button>
        <Button
          data-testid="profile-save"
          type="primary"
          onClick={handleSubmit}
          disabled={loading || !isValid || disableSave}>
          {lang.SAVE_CHANGES}
        </Button>
      </div>
    );
  };

  const renderTopNotification = () => {
    if (type !== PROFILE_TYPES.SELF) return null;

    return dirty || isPopup ? (
      <Notification
        message={isPopup ? lang.POPUP_TITLE : lang.CHANGES_MADE}
        renderButtons={renderNotificationButtons}
      />
    ) : null;
  };

  const renderActionButtons = () => {
    if (type === PROFILE_TYPES.SELF) return null;

    return (
      <div className={styles.actionButtonsContainer}>
        <Button onClick={onFinish}>{lang.DISMISS}</Button>
        <Button
          data-testid="profile-save"
          type="primary"
          onClick={handleSubmit}
          disabled={loading || !dirty || !isValid}>
          {lang.SAVE_CHANGES}
        </Button>
      </div>
    );
  };

  const renderTopSectionFields = () => (
    <>
      {type === PROFILE_TYPES.SELF && (
        <Col span={18}>
          <ScreenTitle title={lang.PAGE_TITLE} containerStyle={styles.noMargin} />
          <Divider />
        </Col>
      )}
      <Col span={24} className={styles.avatarRow}>
        <UserAvatar user={user} size="large" allowEdit />
      </Col>

      <Col span={24}>
        <Row gutter={50}>
          {topSectionFieldsOrder
            .map(field => formComponents.find(f => f.props.name === field))
            .map(({ Component, props }, index) => (
              <Col span={6} key={index}>
                <Component {...props} />
              </Col>
            ))}

          {/* Empty placeholder to maintain layout */}
          {type !== PROFILE_TYPES.SELF && <Col span={6} offset={6} className={styles.buttons} />}
        </Row>
      </Col>
    </>
  );

  const renderFormFields = () =>
    userFieldsOrder
      .map(field => formComponents.find(f => f.props.name === field))
      .map(({ Component, props: initialProps, type: t, hide }, index) => {
        const props = {
          ...initialProps,
          disabled: initialProps.disabled || (!!t && !t?.includes(type)),
        };

        if (hide) return null;

        return (
          <Col span={8} key={index}>
            <Component {...props} />
          </Col>
        );
      });

  const renderBottomSection = () => {
    if (type === PROFILE_TYPES.CONTACT || isPopup) return null;

    return <ProfessionalProfileManager user={user} canEdit={canEdit} />;
  };

  const renderTNC = () => {
    if (!enforceTNC) return null;

    return (
      <>
        <div className={styles.tnc}>
          <Checkbox onChange={changeTNCApproval} className={styles.tncCheckbox} />
          <ReactMarkdown linkTarget="_blank" className={styles.disclaimer}>
            {lang.TERMS_AND_CONDITIONS_DISCLAIMER}
          </ReactMarkdown>
        </div>
        <span className={styles.error}>{errors.approveTermsAndConditions}</span>
      </>
    );
  };

  if (!user) {
    return <Loader />;
  }

  return (
    <>
      {contextHolder}
      <ProfileLayoutComponent
        renderTopNotification={renderTopNotification}
        renderTopSection={renderTopSectionFields}
        renderMainSection={renderFormFields}
        renderBottomSection={renderBottomSection}
        renderFooter={renderTNC}
        renderActionButtons={renderActionButtons}
      />
    </>
  );
};

export { UserProfileComponent, PROFILE_TYPES };
